import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { WEEKDAYS } from "@/lib/calendarUtils";

export default function Availability() {
  const { can } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ workerId: "", weekday: "6", startTime: "00:00", endTime: "23:59", type: "unavailable", note: "" });
  const canManage = can("availability.manage");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get("/availability"),
      can("user.view") ? client.get("/users") : Promise.resolve({ data: [] }),
    ]).then(([av, us]) => {
      setRows(av.data);
      setWorkers((us.data || []).filter((u) => u.role === "cleaner" || u.role === "supervisor"));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [can]);
  useEffect(load, [load]);

  const workerName = (id) => workers.find((w) => w.id === id)?.displayName || id;

  const add = async () => {
    setBusy(true);
    try {
      await client.post("/availability", { ...form, weekday: Number(form.weekday) });
      toast.success(t("toast.availAdded", "Availability added"));
      setForm({ workerId: "", weekday: "6", startTime: "00:00", endTime: "23:59", type: "unavailable", note: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await client.delete(`/availability/${id}`); toast.success(t("toast.availRemoved", "Removed")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="availability-tab">
      <div>
        <h3 className="font-display text-lg font-bold text-slate-900">{t("avail.title", "Worker availability")}</h3>
        <p className="text-sm text-slate-500">{t("avail.subtitle", "Recurring weekly unavailable blocks feed conflict detection.")}</p>
      </div>

      {canManage && (
        <Card className="border-slate-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>{t("avail.worker", "Worker")}</Label>
              <Select value={form.workerId} onValueChange={(v) => setForm({ ...form, workerId: v })}>
                <SelectTrigger data-testid="avail-worker-select" className="mt-1.5"><SelectValue placeholder={t("cal.worker", "Worker")} /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.displayName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("avail.weekday", "Weekday")}</Label>
              <Select value={form.weekday} onValueChange={(v) => setForm({ ...form, weekday: v })}>
                <SelectTrigger data-testid="avail-weekday-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d) => <SelectItem key={d} value={String(d)}>{t(`wd.${d}`, ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][d])}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("cal.start", "Start")}</Label><Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("cal.end", "End")}</Label><Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="mt-1.5" /></div>
            <Button data-testid="avail-add-btn" onClick={add} disabled={busy || !form.workerId} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" />{t("avail.add", "Add block")}</>}
            </Button>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        {loading ? <p className="p-6 text-sm text-slate-500">{t("common.loading", "Loading…")}</p>
          : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500"><CalendarClock className="h-8 w-8 mx-auto mb-2 text-slate-300" />{t("avail.none", "No availability rules yet.")}</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.id} data-testid={`avail-row-${r.id}`} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={r.type === "unavailable" ? "border-red-200 text-red-600" : "border-emerald-200 text-emerald-600"}>
                      {t(`avail.${r.type}`, r.type)}
                    </Badge>
                    <span className="font-medium text-slate-800">{workerName(r.workerId)}</span>
                    <span className="text-sm text-slate-500">{t(`wd.${r.weekday}`, r.weekday)} · {r.startTime}–{r.endTime}</span>
                    {r.note && <span className="text-xs text-slate-400">{r.note}</span>}
                  </div>
                  {canManage && (
                    <Button size="icon" variant="ghost" className="text-red-500" data-testid={`avail-delete-${r.id}`} onClick={() => remove(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}
